/**
/**
 * ShowDiploma Component
 *
 * Displays a button to view the course diploma and a modal to preview the diploma PDF.
 * Handles diploma retrieval via API and passes relevant props to child components for dynamic rendering.
 *
 * Features:
 * - Conditional rendering of diploma button based on certificate data
 * - Handles diploma modal open/close state
 * - Fetches diploma URL via API call
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.dataCertificate - Certificate data object containing diploma info
 * @param {string} props.titleCourse - Title of the course for diploma display
 * @returns {JSX.Element} The rendered diploma section
 */
import { useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import ModalViewPDF from 'components/UI/ModalViewPDF/ModalViewPDF';
import classes from './ShowDiploma.module.css';

const ShowDiploma = props => {
  const [openDiploma, setOpenDiploma] = useState(false);
  const [urlDiploma, setUrlDiploma] = useState('');

  /**
   * Handler to open the diploma modal and fetch diploma URL
   * Passed as prop to Button and invoked on click
   * @param {string} url - API endpoint for diploma
   * @returns {Function}
   */
  const openDiplomaHandler = url => {
    return () => {
      axios({
        url,
        method: 'GET'
      }).then(response => {
        console.log('🚀 ~ openDiplomaHandler ~ response:', response);
        if (response.data.target) {
          window.open(response.data.location, '_blank');
        } else {
          const urlDiploma = response.data.location;
          setOpenDiploma(true);
          setUrlDiploma(urlDiploma);
        }
      });
    };
  };

  /**
   * Handler to close the diploma modal
   * Passed as prop to ModalViewPDF
   */
  const closeDiplomaHandler = () => {
    setOpenDiploma(false);
  };

  // TODO revisar que se muestre el botón del diploma hasta que el usuario termine el curso

  return (
    <aside className={classes.showDiplomaContainer}>
      {/* Conditional rendering: diploma button if certificate and diploma exist */}
      {props.dataCertificate && props.dataCertificate.isDiploma && (
        <Button
          className={classes.showDiplomaBtn} // Prop passed: button styling
          onClick={openDiplomaHandler(props.dataCertificate.url)} // Prop passed: click handler
        >
          <figure className={classes.iconIncludesContainer}>
            <img
              className={classes.img} // Prop passed: image styling
              src="https://d1g6c01s9ssd7h.cloudfront.net/img/consulta_diploma.png" // Prop passed: diploma image URL
            />
          </figure>
          Consulta tu diploma aquí
        </Button>
      )}
      {/* ModalViewPDF: diploma preview modal */}
      <ModalViewPDF
        urlCloudFront={urlDiploma} // Prop passed: diploma PDF URL
        openModal={openDiploma} // Prop passed: modal open state
        onCloseModal={closeDiplomaHandler} // Prop passed: close handler
        urlViewPDF={urlDiploma} // Prop passed: diploma PDF URL
        titlePDF="Vista del Diploma" // Prop passed: modal title
        titleCourse={props.titleCourse} // Prop passed: course title
        endDate={props.dataCertificate?.endDate} // Prop passed: diploma end date
        URLShare={props.dataCertificate?.urlShare} // Prop passed: diploma share URL
      />
    </aside>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
ShowDiploma.propTypes = {
  /**
   * Certificate data object containing diploma info
   * Passed as prop to Button and ModalViewPDF
   */
  dataCertificate: PropTypes.object,
  /**
   * Title of the course for diploma display
   * Passed as prop to ModalViewPDF
   */
  titleCourse: PropTypes.string
};

ShowDiploma.defaultProps = {
  dataCertificate: {},
  titleCourse: ''
};

// ...existing code...

ShowDiploma.propTypes = {
  // eslint-disable-next-line react/require-default-props
  dataCertificate: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  titleCourse: PropTypes.string
};

export default ShowDiploma;
